/**
 * Initialization Service
 * Handles seed data setup and storage initialization
 */

import { mockData } from '@/lib/seed-data'
import {
  checkStorageVersion,
  isStorageInitialized,
  markStorageInitialized,
  setAll,
  StorageKeys,
} from './storage'
import {
  cleanUser,
  cleanUsers,
  cleanWorkspace,
  cleanTask,
  cleanMessage,
  cleanDocument,
} from '@/lib/utils/clean-references'

/**
 * Initialize storage with seed data
 * This function is called on app startup and seeds the application with sample data
 * unless storage has already been initialized (e.g., by a user signup)
 */
export function initializeStorage(): void {
  // Check storage version
  checkStorageVersion()

  // Check if already initialized
  if (isStorageInitialized()) {
    return
  }

  try {
    // Generate and load seed data
    const seedData = mockData

    // Strip circular references by keeping only essential data
    // Users: remove all relationship arrays
    const cleanedUsers = cleanUsers(seedData.users)

    // Clean tasks first - preserve basic relationships but remove circular refs
    // Note: cleanTask already handles nested relationships safely
    const cleanedTasks = seedData.tasks.map((task) => cleanTask(task))

    // Clean projects - preserve workspace association but remove circular refs
    // Include tasks that belong to each project
    const cleanedProjects = seedData.projects.map((proj) => {
      const projectTasks = cleanedTasks.filter((task) =>
        task.projects.some((p) => p.id === proj.id)
      )

      return {
        ...proj,
        workspace: cleanWorkspace(proj.workspace),
        creator: cleanUser(proj.creator),
        users: cleanUsers(proj.users),
        tasks: projectTasks,
        messages: [],
        documents: [],
      }
    })

    // Workspaces: keep user references and populate with tasks
    // Include tasks that belong to each workspace
    const cleanedWorkspaces = seedData.workspaces.map((ws) => {
      const workspaceTasks = cleanedTasks.filter(
        (task) => task && task.workspace && task.workspace.id === ws.id
      )
      const workspaceProjects = cleanedProjects.filter(
        (proj) => proj && proj.workspace && proj.workspace.id === ws.id
      )

      return {
        ...cleanWorkspace(ws),
        tasks: workspaceTasks,
        projects: workspaceProjects,
      }
    })

    // Clean messages - preserve basic relationships but remove circular refs
    const cleanedMessages = seedData.messages.map((message) => cleanMessage(message))

    // Clean documents - preserve basic relationships but remove circular refs
    const cleanedDocuments = seedData.documents.map((document) => cleanDocument(document))

    // Save to localStorage with cleaned data
    setAll(StorageKeys.USERS, cleanedUsers)
    setAll(StorageKeys.WORKSPACES, cleanedWorkspaces)
    setAll(StorageKeys.PROJECTS, cleanedProjects)
    setAll(StorageKeys.TASKS, cleanedTasks)
    setAll(StorageKeys.MESSAGES, cleanedMessages)
    setAll(StorageKeys.DOCUMENTS, cleanedDocuments)

    // Mark as initialized
    markStorageInitialized()
  } catch (error) {
    console.error('❌ Error initializing storage:', error)
    throw error
  }
}

/**
 * Reset storage (for testing)
 */
export function resetStorage(): void {
  localStorage.clear()
  initializeStorage()
}
